import React, { useRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { NetworkStatus } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { insm } from '@atlaskit/insm';
import Spinner from '@atlaskit/spinner/spinner';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { updatePageLoadInteractionName } from '@atlaskit/react-ufo/interaction-metrics';

import { MovePageSuccessFlag } from '@confluence/page-tree/entry-points/MovePageSuccessFlag.tsx';
import { useFeatureCodesInURL } from '@confluence/feature-codes/entry-points/useFeatureCodesInURL.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { expValEquals, expVal } from '@confluence/feature-experiments';
import type { LoadingPriority } from '@confluence/loadable';
import { useGetCommentLinkType } from '@confluence/comments-util/entry-points/useGetCommentLinkType.ts';
import {
	CREATE_PAGE_EXPERIENCE,
	VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
	VIEW_PAGE_SESSION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { useExperienceState } from '@confluence/experience-tracker/entry-points/useExperienceState.ts';
import { FullPageEditorLoader } from '@confluence/full-page-editor/entry-points/FullPageEditorLoader.ts';
import { TreeRootForServerPlaceholderWrapper } from '@confluence/full-page-editor/entry-points/TreeRootForServerPlaceholderWrapper.ts';
import type { NativeCollabInjectedProps } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import { NativeCollabLoader } from '@confluence/native-collab/entry-points/NativeCollabLoader.ts';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent.ts';
import { useLivePageStoreActions } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { LivePageInviteNudge } from '@confluence/onboarding-editor/entry-points/components/LivePageInviteNudge.ts';
import { useLivePageOnboardingNudgeEligible } from '@confluence/onboarding-editor/entry-points/hooks/useLivePageOnboardingNudgeEligible.ts';
import { fg } from '@confluence/feature-gating';
import { NewCommentsProvider } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import { PageCommentsCountProvider } from '@confluence/comment-context/entry-points/PageCommentsCountContext.ts';
import {
	OnboardingNthUserContentTypeSpotlight,
	ContentType,
} from '@confluence/onboarding-nth-user-setup-modal/entry-points/OnboardingNthUserContentTypeSpotlight.tsx';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { getGlobalTransitionPreloader } from '@confluence/route-manager/entry-points/getGlobalTransitionPreloader.ts';
import { StartLivePageLoad } from '@confluence/live-pages-perf/entry-points/live-pages-perf.config.ts';
import { FullCycleRenderWrapper } from '@confluence/ssr-utilities/entry-points/FullCycleRender.ts';
import { RemoteLinkFlag } from '@confluence/experiment-jira-smart-links/entry-points/RemoteLinkFlagEntryPoint.ts';
import { MarkdownModeViewContainer } from '@confluence/markdown-mode-view-toggle/entry-points/useMarkdownModeView.ts';
import { MarkdownModeViewToggle } from '@confluence/markdown-mode-view-toggle/entry-points/MarkdownModeViewToggle.ts';
import { useIsMarkdownMode } from '@confluence/content-markdown-mode/entry-points/useIsMarkdownMode.ts';
import {
	CONTENT_RIGHT_PADDING,
	useBehavior,
	useIsObjectSidebarShown,
	usePanelWidth,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';

import { useEditorDataLoader } from './useEditorDataLoader.tsx';
import { triggerViewedAnalytics } from './LivePageViewedAnalytics.ts';
import { usePermissionCheck } from './usePermissionCheck.tsx';
import { LivePageSessionExperienceStart } from './LivePageSessionExperienceStart.tsx';
import { LivePageShortcuts } from './LivePageShortcuts.tsx';

export type LivePageComponentProps = {
	contentId: string;
	spaceKey: string | undefined;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	customPerfData?: Record<string, string | boolean | object>;
	loadingPriority?: LoadingPriority | null;

	// When LivePageComponent goes from not fast loaded -> fast loaded or vice-versa, ViewPageCommon remounts LivePageComponent (via renderLivePageEarly).
	// We need to inform LivePageContent if we're likely to remount, as that could cause NCS issues.
	isFastLoaded?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
	hasPageComments: boolean;
	hasInlineComments: boolean;
};
type LivePageContentProps = {
	canEdit: boolean;
	isSeamlessTransition: boolean;
	refetch: () => void;
	onNativeCollabLoad?: (contentId: string, viewMode: boolean | undefined) => void;
	onDraftSyncSuccess?: () => void; // Used to hook into the editor draft sync mutation
} & LivePageComponentProps;

const spinnerContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	height: '100vh',
});

/**
 * EDITOR-6558: Styles for the Markdown Mode view-mode toggle.
 *
 * `editorWrapperStyles` is unconditional: every render of `LivePageEditorBody`
 * mounts the wrapper `<Box>` so that flipping `useIsMarkdownMode` doesn't
 * change the tree shape and remount the editor.
 *
 * `editorWrapperWithToggleStyles` is composed on top of it ONLY when the
 * floating toggle is actually rendered, so the control cohort gets a plain
 * unstyled `<div>` (no `position: relative`, no new stacking context).
 */
const editorWrapperStyles = xcss({});

const editorWrapperWithToggleStyles = xcss({
	position: 'relative',
});

/** Extra inset after full store `panelWidth` when the object sidebar uses **cover** behavior. */
const OBJECT_SIDEBAR_COVER_MODE_TOGGLE_GAP_PX = 28;

/** Tail gap after visible panel width in **push** mode (see `CONTENT_RIGHT_PADDING` in object-sidebar store). */
const OBJECT_SIDEBAR_PUSH_MODE_TOGGLE_GAP_PX = 8;

/**
 * Parent sets this when the floating markdown toggle is shown (see `LivePageEditorBody`).
 * Same pattern as `--object-sidebar-push-width` in ObjectSidebarPushPlaceholder.
 */
const MARKDOWN_TOGGLE_OBJECT_SIDEBAR_OFFSET_VAR = '--markdown-toggle-object-sidebar-offset';

const floatingViewModeToggleStyles = xcss({
	position: 'absolute',
	// Clear the page header row (Edited / Share / …).
	top: 'space.800', // 56px (header) + 8px spacing
	// Horizontal shift when object sidebar is open: parent sets MARKDOWN_TOGGLE_OBJECT_SIDEBAR_OFFSET_VAR (EDITOR-6558).
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	right: `calc(${token('space.300')} + var(${MARKDOWN_TOGGLE_OBJECT_SIDEBAR_OFFSET_VAR}, 0px))`,
	zIndex: 'blanket',
	display: 'flex',
	alignItems: 'center',
	padding: 'space.050',
	backgroundColor: 'elevation.surface',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.large',
});

// Handle aborting experiences
/* Why we're aborting each experience:
 * VIEW_PAGE_SESSION_EXPERIENCE: Aborted due to not wanting live pages to affect classic view-page-specific experience. Live pages have their own live-page-session experience.
 * VIEW_PAGE_EDIT_BUTTON_EXPERIENCE: Aborted due to editable live page starting but never finishing view-page/edit, because in ViewPageCommon, stickyHeader briefly mounts while loading `globalLiveState.isLive` and `editPermission`, which we unmount after we determine doc is an editable live page
 */
const useAbortNonLiveExperiences = () => {
	const experienceTracker = React.useContext(ExperienceTrackerContext);
	const { hasStopped: hasViewPageSessionExperienceStopped } =
		useExperienceState(VIEW_PAGE_SESSION_EXPERIENCE) || {};
	const { hasStopped: hasViewPageEditButtonExperienceStopped } =
		useExperienceState(VIEW_PAGE_EDIT_BUTTON_EXPERIENCE) || {};

	const nonCoreExperiencesToAbort = [
		{ name: VIEW_PAGE_SESSION_EXPERIENCE, hasStopped: hasViewPageSessionExperienceStopped },
		{
			name: VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
			hasStopped: hasViewPageEditButtonExperienceStopped,
		},
	];

	nonCoreExperiencesToAbort.forEach((abortedExperience) => {
		if (!abortedExperience.hasStopped) {
			experienceTracker.abort({
				name: abortedExperience.name,
				reason: 'Live Page Editor was opened',
			});
		}
	});
};

export const LivePageContent = ({
	contentId,
	spaceKey,
	canEdit,
	refetch,
	ClassicEditorContextProviderLoader,
	onNativeCollabLoad,
	customPerfData,
	onDraftSyncSuccess,
	loadingPriority,
	isSeamlessTransition,
	isFastLoaded,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
	hasPageComments,
	hasInlineComments,
}: LivePageContentProps) => {
	const { loomExternalOpenRoute } = useEmbeddedViewPageFeatures();

	useAbortNonLiveExperiences();

	const lastNativeCollabKeyRef = useRef(contentId);
	// NativeCollabComponent MUST remount if we're not a seamless transition, and it MUST NOT remount if we are a seamless transition.
	// Changing the key triggers a remount.
	// This means the key needs to stay the same if we go from non-seamless transition -> seamless transition
	// This fixes a bug where NativeCollabComponent unmounting on transition from non-seamless -> seamless would
	//   bug out the collab provider editor plugin, preventing NCS from connecting
	const nativeCollabKey = useMemo(() => {
		if (!isSeamlessTransition) {
			lastNativeCollabKeyRef.current = contentId;
			return contentId;
		}
		return lastNativeCollabKeyRef.current;
	}, [contentId, isSeamlessTransition]);

	const LivePageWrapper = ClassicEditorContextProviderLoader ?? React.Fragment;

	// EDITOR-6558: Gate all Markdown Mode scaffolding (sweet-state container,
	// floating toggle, MutationObserver) on the `cc-markdown-mode` experiment
	// + per-page check. `useIsMarkdownMode` is synchronous: it skips entirely
	// when the experiment is off and otherwise reads from the cache populated
	// by `ContentHeaderUnifiedQuery` upstream, so there is no flicker.
	//
	// When `false` (control cohort or non-markdown page), `LivePageContent`
	// renders DOM-identical output to its pre-EDITOR-6558 shape (the
	// `LivePageContent → LivePageEditorBody` split adds one extra React
	// component layer to the fiber tree, but the rendered DOM is unchanged —
	// no `MarkdownModeViewContainer`, no wrapper `Box`, no floating toggle,
	// and no `MutationObserver`).
	const isMarkdownMode = useIsMarkdownMode(contentId);

	const body = (
		<LivePageEditorBody
			contentId={contentId}
			spaceKey={spaceKey}
			canEdit={canEdit}
			refetch={refetch}
			onNativeCollabLoad={onNativeCollabLoad}
			customPerfData={customPerfData}
			onDraftSyncSuccess={onDraftSyncSuccess}
			loadingPriority={loadingPriority}
			isSeamlessTransition={isSeamlessTransition}
			isFastLoaded={isFastLoaded}
			hasByLineContributors={hasByLineContributors}
			hasByLineExtensions={hasByLineExtensions}
			hasReactions={hasReactions}
			hasPageComments={hasPageComments}
			hasInlineComments={hasInlineComments}
			ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
			LivePageWrapper={LivePageWrapper}
			nativeCollabKey={nativeCollabKey}
			loomExternalOpenRoute={loomExternalOpenRoute}
			isMarkdownMode={isMarkdownMode}
		/>
	);

	// `MarkdownModeViewContainer` is mounted here so the view-mode toggle
	// (rendered up in `useHeaderItems.tsx`) and the editor body share a single
	// `contentId`-scoped sweet-state store. Navigating between pages causes the
	// scope to change and resets the view to the default `'wysiwyg'`.
	//
	// `useMarkdownModeView()` is called inside `LivePageEditorBody` (a separate
	// component below) so it runs within the container's React context — calling
	// the hook here (above the container in the tree) would read from the wrong scope.
	//
	// EDITOR-6558 fix: mount the container unconditionally — DO NOT branch on
	// `isMarkdownMode`. Previously this was wrapped in `if (!isMarkdownMode)
	// return body` which changed the React tree shape the instant
	// `useIsMarkdownMode` flipped (e.g. right after the user clicks "Convert
	// this page" in the conversion confirmation dialog). React reconciles
	// positionally, so swapping the parent of `body` from "no wrapper" to
	// `MarkdownModeViewContainer` unmounted the entire `NativeCollabLoader`
	// + editor subtree, tore down the NCS connection, and surfaced the
	// "Give us a few seconds to reconnect…" error until the next refresh.
	//
	// `MarkdownModeViewContainer` is a `react-sweet-state` `createContainer`
	// — it renders no DOM, just a Context provider — so the control cohort
	// is byte-identical at the DOM level.
	return <MarkdownModeViewContainer scope={contentId}>{body}</MarkdownModeViewContainer>;
};

// Props for the inner body component that runs inside MarkdownModeViewContainer.
type LivePageEditorBodyProps = LivePageContentProps & {
	LivePageWrapper: React.ComponentType<any> | typeof React.Fragment;
	nativeCollabKey: string;
	loomExternalOpenRoute: string | undefined;
	// EDITOR-6558: When `false`, the body skips all Markdown Mode scaffolding
	// (floating toggle, MutationObserver, wrapper Box) and renders the original
	// pre-EDITOR-6558 tree shape directly under `LivePageWrapper`.
	isMarkdownMode: boolean;
};

/**
 * Inner component intentionally rendered *inside* `MarkdownModeViewContainer`
 * so `useMarkdownModeView()` reads from the correct contentId-scoped store.
 *
 * Branches on `view` to render:
 *  - `'wysiwyg'` (default): the existing NativeCollabLoader + FullPageEditorLoader stack.
 *  - `'source'`:  markdown source editor (handled by the editor plugin).
 *  - `'preview'`: lightweight preview view (handled by the editor plugin).
 *
 * The markdown mode orchestration (snapshot logic, dispatch transaction tap,
 * source/preview rendering, writeback handlers, echo guards) is now handled by
 * the editor plugin (@atlassian/editor-plugin-markdown-mode) as a contentComponent.
 *
 * This component still renders the floating toggle fallback (when toolbar is undocked)
 * and provides the isWysiwyg CSS class for layout.
 */
const LivePageEditorBody = ({
	contentId,
	spaceKey,
	canEdit,
	refetch,
	ClassicEditorContextProviderLoader,
	onNativeCollabLoad,
	customPerfData,
	onDraftSyncSuccess,
	loadingPriority,
	isSeamlessTransition,
	isFastLoaded,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
	hasPageComments,
	hasInlineComments,
	LivePageWrapper,
	nativeCollabKey,
	loomExternalOpenRoute,
	isMarkdownMode,
}: LivePageEditorBodyProps) => {
	// EDITOR-6558: Check whether the editor's primary toolbar is currently
	// rendered (docked). When docked, the toolbar section registration in
	// `markdownModeViewTogglePlugin` handles the toggle, so we hide the
	// floating one. When undocked (the default for live pages), the toolbar
	// isn't rendered and the floating toggle takes over.
	//
	// Only run when `isMarkdownMode` — for the control cohort and non-markdown
	// pages we skip the MutationObserver entirely. The observer watches the
	// entire `document.body` subtree for childList changes and would otherwise
	// fire on every DOM mutation in the page.
	const [isToolbarDocked, setIsToolbarDocked] = useState(false);
	useLayoutEffect(() => {
		if (!isMarkdownMode) {
			return;
		}
		const check = () => {
			const el = document.querySelector('[data-testid="markdown-mode-view-toggle-section"]');
			setIsToolbarDocked(!!el);
		};
		check();
		const observer = new MutationObserver(check);
		observer.observe(document.body, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, [isMarkdownMode]);

	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panelWidth = usePanelWidth();
	const objectSidebarBehavior = useBehavior();

	// If not seamless transition, ensure key is passed to NativeCollabLoader so it can clean up NCS provider on unmount when contentId changes
	const livePageInner = (
		<LivePageWrapper>
			{!isSeamlessTransition && <StartLivePageLoad contentId={contentId} viewMode={!canEdit} />}
			<ContentViewedEvent contentId={contentId} />
			<ExperienceSuccess name={CREATE_PAGE_EXPERIENCE} attributes={{ isLivePage: true }} />
			<LivePageSessionExperienceStart />
			<NativeCollabLoader
				contentId={contentId}
				spaceKey={spaceKey}
				contentType="page"
				key={nativeCollabKey}
				onPermissionChange={refetch}
				canEdit={canEdit}
			>
				{(propsFromNativeCollab: NativeCollabInjectedProps) => {
					const { viewMode } = propsFromNativeCollab;
					const livePageCustomPerfData = {
						...customPerfData,
						viewMode,
					};
					// TODO MODES-3871 Remove this triggerViewedAnalytics side effect when we cleanup LivePageComponent.
					// Trigger live page view analytics event once when the user views a live page.
					onNativeCollabLoad?.(contentId, viewMode);

					return (
						// This TreeRootForServerPlaceholderWrapper placed here to ensure the proper lookup of the placeholders for
						// SSRed loadables. Due to different component trees between server (LivePageComponent) and client (LivePageComponentServer)
						// the placeholder ids hierarchy is different. This wrapper terminates the lookup for placeholder ids on the common parent,
						// so it matches on both server and client.
						expValEquals('platform_editor_editor_ssr_streaming', 'isEnabled', true) ? (
							<TreeRootForServerPlaceholderWrapper>
								<FullPageEditorLoader
									{...propsFromNativeCollab}
									extensionContextProvider={{
										ClassicEditorContextProvider: ClassicEditorContextProviderLoader,
									}}
									hasEditPermission={canEdit}
									customPerfData={livePageCustomPerfData}
									onDraftSyncSuccess={onDraftSyncSuccess}
									loadingPriority={loadingPriority}
									isFastLoadedLiveDoc={isFastLoaded}
									hasByLineContributors={hasByLineContributors}
									hasByLineExtensions={hasByLineExtensions}
									hasReactions={hasReactions}
									hasPageComments={hasPageComments}
									hasInlineComments={hasInlineComments}
									loomExternalOpenRoute={loomExternalOpenRoute}
								/>
								<LivePageShortcuts canEdit={canEdit} />
							</TreeRootForServerPlaceholderWrapper>
						) : (
							<FullCycleRenderWrapper>
								<TreeRootForServerPlaceholderWrapper>
									<FullPageEditorLoader
										{...propsFromNativeCollab}
										extensionContextProvider={{
											ClassicEditorContextProvider: ClassicEditorContextProviderLoader,
										}}
										hasEditPermission={canEdit}
										customPerfData={livePageCustomPerfData}
										onDraftSyncSuccess={onDraftSyncSuccess}
										loadingPriority={loadingPriority}
										isFastLoadedLiveDoc={isFastLoaded}
										hasByLineContributors={hasByLineContributors}
										hasByLineExtensions={hasByLineExtensions}
										hasReactions={hasReactions}
										hasPageComments={hasPageComments}
										hasInlineComments={hasInlineComments}
										loomExternalOpenRoute={loomExternalOpenRoute}
									/>
									<LivePageShortcuts canEdit={canEdit} />
								</TreeRootForServerPlaceholderWrapper>
							</FullCycleRenderWrapper>
						)
					);
				}}
			</NativeCollabLoader>
		</LivePageWrapper>
	);

	// EDITOR-6558: Always render the wrapper Box regardless of `isMarkdownMode`,
	// and conditionally compose `position: relative` on top of its base styles
	// only when the floating toggle is also rendered. Previously the body
	// branched between two different tree shapes (bare `livePageInner` vs.
	// wrapped) — which caused React to unmount/remount the entire
	// `NativeCollabLoader` + editor subtree the instant `useIsMarkdownMode`
	// flipped (e.g. right after the user clicks "Convert this page" in the
	// conversion confirmation dialog). The remount tore down the NCS connection
	// and surfaced the "Give us a few seconds to reconnect…" error until the
	// next refresh.
	//
	// `xcss` accepts an array of style objects — the second one is only
	// included when the toggle will be rendered, so the control cohort sees a
	// plain unstyled `<div>` ancestor (no `position: relative`, no new stacking
	// context, no padding/background/border).
	//
	// The `position: relative` MUST live on the wrapper that contains
	// `livePageInner` (not on a smaller anchor around the toggle alone),
	// because the floating toggle is `position: absolute` with `top`/`right`
	// offsets that anchor against the full editor area.
	const showFloatingToggle = isMarkdownMode && !isToolbarDocked;

	const editorWrapperMarkdownToggleVars = useMemo((): React.CSSProperties | undefined => {
		if (!showFloatingToggle) {
			return undefined;
		}
		let horizontalShiftPx = 0;
		if (isObjectSidebarShown) {
			if (objectSidebarBehavior === 'cover') {
				horizontalShiftPx = panelWidth + OBJECT_SIDEBAR_COVER_MODE_TOGGLE_GAP_PX;
			} else {
				const visiblePanelWidth = Math.max(0, panelWidth - CONTENT_RIGHT_PADDING);
				horizontalShiftPx = visiblePanelWidth + OBJECT_SIDEBAR_PUSH_MODE_TOGGLE_GAP_PX;
			}
		}
		return {
			[MARKDOWN_TOGGLE_OBJECT_SIDEBAR_OFFSET_VAR]: `${horizontalShiftPx}px`,
		} as React.CSSProperties;
	}, [showFloatingToggle, isObjectSidebarShown, objectSidebarBehavior, panelWidth]);

	return (
		<Box
			xcss={[editorWrapperStyles, showFloatingToggle && editorWrapperWithToggleStyles]}
			testId="markdown-mode-floating-toggle-anchor"
			// Runtime-driven CSS custom property for the floating toggle `right` (see `floatingViewModeToggleStyles`).
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={editorWrapperMarkdownToggleVars}
		>
			{showFloatingToggle && (
				<Box xcss={floatingViewModeToggleStyles} testId="markdown-mode-view-toggle-floating">
					<MarkdownModeViewToggle contentId={contentId} />
				</Box>
			)}
			{livePageInner}
		</Box>
	);
};

export const LivePageComponent = ({
	contentId,
	spaceKey,
	ClassicEditorContextProviderLoader,
	customPerfData,
	loadingPriority,
	isFastLoaded,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
	hasPageComments,
	hasInlineComments,
}: LivePageComponentProps) => {
	const {
		canEdit,
		loading: isLoadingPermissionCheck,
		contentQueryError,
		refetch,
		networkStatus,
	} = usePermissionCheck(contentId);
	const prevCanEditRef = useRef<boolean | undefined>(undefined);
	const hasSentViewedAnalytics = useRef<boolean>(false);

	const { setMode } = useLivePageStoreActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isPreloading } = useEditorDataLoader({
		isLoadingPermissionCheck,
		contentId,
		spaceKey,
		canEdit,
	});
	const featureCodes = useFeatureCodesInURL();
	const commentLink = useGetCommentLinkType();

	const { showLivePageInviteFlag } = useLivePageOnboardingNudgeEligible({ contentId });

	const transitionPreloaderCurrentCallbackIDs =
		getGlobalTransitionPreloader().getCurrentRouteCallbackIds();
	const isSeamlessTransition = fg('confluence_frontend_transition_preloading_system')
		? transitionPreloaderCurrentCallbackIDs.includes('live_doc_preloading_transition_callback_id')
		: false;

	useEffect(() => {
		if (!expValEquals('confluence_dedupe_page_viewed_event', 'isEnabled', true)) {
			hasSentViewedAnalytics.current = false;
		}

		return () => {
			// Reset the mode and analytics flag when contentId changes or component unmounts
			void setMode(undefined);
			prevCanEditRef.current = undefined;
			if (expValEquals('confluence_dedupe_page_viewed_event', 'isEnabled', true)) {
				hasSentViewedAnalytics.current = false;
			}
		};
	}, [contentId, setMode]);

	useEffect(() => {
		// Permissions on a page can change at anytime. Should update the mode if the new permissions are different from the previous ones.
		const hasPermissionsChanged = canEdit !== prevCanEditRef.current;
		if (hasPermissionsChanged) {
			void setMode(canEdit ? 'edit' : 'view');
		}
		prevCanEditRef.current = canEdit;
	}, [canEdit, contentId, setMode]);

	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const liveEditUFOName = isEmbeddedConfluence ? 'embedded-live-edit' : 'live-edit';

	const { overrideUFORouteName } = useRouteActions();
	useEffect(() => {
		overrideUFORouteName(liveEditUFOName);
		// overrideUFORouteName only updates the trace-based UFO system. For embedded
		// pages, we also need to update the legacy interaction-metrics system, which
		// was set to 'embedded-view-page' by EmbeddedViewPage.
		if (isEmbeddedConfluence) {
			updatePageLoadInteractionName(liveEditUFOName);
		}
		if (expValEquals('cc_editor_interactivity_monitoring', 'liveDocs', true)) {
			insm.overrideExperienceKey(liveEditUFOName);
		}
		// Live doc transitions trigger a UFO route change but usually don't trigger a LivePageComponent remount, so we need to re-run on contentId change
	}, [overrideUFORouteName, contentId, liveEditUFOName, isEmbeddedConfluence]);

	const handleNativeCollabLoad = (contentId: string, viewMode: boolean | undefined) => {
		if (hasSentViewedAnalytics.current === false) {
			void triggerViewedAnalytics({
				contentId,
				editPermission: canEdit,
				createAnalyticsEvent,
				viewMode,
				...(featureCodes && featureCodes.from ? { from: featureCodes.from } : {}),
				...(commentLink && { commentLink }),
			});
			hasSentViewedAnalytics.current = true;
		}
	};

	if (contentQueryError) {
		return <ErrorDisplay error={contentQueryError} />;
	}

	const skipLoadingScreenWhenSSRed = window.__SSR_RENDERED__;
	if (
		((isPreloading && !skipLoadingScreenWhenSSRed) ||
			// don't unmount live pages editor on permission refetch
			(isLoadingPermissionCheck && networkStatus !== NetworkStatus.refetch)) &&
		!isSeamlessTransition
	) {
		return (
			<Box xcss={spinnerContainerStyles}>
				<Spinner interactionName="live-pages-loading-spinner" size="medium" />
			</Box>
		);
	}

	return (
		<NewCommentsProvider>
			<PageCommentsCountProvider contentId={contentId}>
				{expVal('confluence_modernized_move_dialog', 'isEnabled', false) && <MovePageSuccessFlag />}
				{fg('cc_fd_jira_issue_live_doc_prompt_switch') && <RemoteLinkFlag contentId={contentId} />}
				<LivePageContent
					contentId={contentId}
					spaceKey={spaceKey}
					ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
					canEdit={canEdit}
					refetch={refetch}
					onNativeCollabLoad={handleNativeCollabLoad}
					onDraftSyncSuccess={showLivePageInviteFlag}
					customPerfData={customPerfData}
					loadingPriority={loadingPriority}
					isSeamlessTransition={isSeamlessTransition}
					isFastLoaded={isFastLoaded}
					hasByLineContributors={hasByLineContributors}
					hasByLineExtensions={hasByLineExtensions}
					hasReactions={hasReactions}
					hasPageComments={hasPageComments}
					hasInlineComments={hasInlineComments}
				/>
				<OnboardingNthUserContentTypeSpotlight
					contentType={ContentType.LIVE_DOC}
					contentId={contentId}
				/>
				<LivePageInviteNudge />
			</PageCommentsCountProvider>
		</NewCommentsProvider>
	);
};
